import { useState, useEffect } from 'react'
import SEO from '../components/SEO'

// Gallery image interface (matches API response)
interface GalleryImage {
  id: string
  name: string
  title: string
  description: string
  category: string
  tags: string[]
  isActive: boolean
  downloadUrl: string
  thumbnailUrl: string
  libraryId: string
  eTag: string
  lastModified: string
}

function ArtGallery() {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const itemsPerPage = 12 // Show 12 images per page

  useEffect(() => {
    const loadGalleryImages = async () => {
      setIsLoading(true)
      setError(null)
      console.log('ArtGallery: Fetching gallery images from API...')

      try {
        // Fetch gallery images from API (on-demand, no cache)
        const response = await fetch('/.netlify/functions/gallery-metadata')

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()

        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch gallery images')
        }

        console.log('ArtGallery: Loaded', data.images.length, 'gallery images from API')
        // Images are already filtered to isActive=true by the API
        setGalleryImages(data.images)
      } catch (error) {
        console.error('ArtGallery: Error loading gallery images:', error)
        setError(error instanceof Error ? error.message : 'Failed to load gallery images')
        setGalleryImages([])
      } finally {
        setIsLoading(false)
      }
    }

    // Initial load
    loadGalleryImages()
  }, [])

  // Get unique categories from images
  const categories = ['All', ...new Set(galleryImages.map(img => img.category || 'Uncategorized'))]

  // Filter gallery images
  const filteredImages = galleryImages.filter(img => {
    const categoryMatch = selectedCategory === 'All' || img.category === selectedCategory
    const searchMatch = searchTerm === '' ||
      (img.title && img.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (img.description && img.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (img.tags && img.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    return categoryMatch && searchMatch
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredImages.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentImages = filteredImages.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCategory, searchTerm])

  return (
    <div className="py-5">
      <SEO
        title="Art Gallery - Original Artwork from Local Artists"
        description="Explore our curated collection featuring original works from over 75 talented local artists at The Salty Mermaid in Ormond Beach, FL. Each piece captures the beauty and spirit of coastal Florida."
        canonicalUrl="https://saltymermaidart.com/gallery"
      />
      <div className="container">
        <h1 className="display-4 fw-bold text-center mb-4">Art Gallery</h1>
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  Explore our curated collection featuring original works from over 75 talented local artists.
                  Each piece captures the beauty and spirit of coastal Florida.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="row mb-5 justify-content-center">
          <div className="col-md-10">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Search Artwork:</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                      placeholder="Search by title or description..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Category:</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {!isLoading && filteredImages.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredImages.length)} of {filteredImages.length} images
                  {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                </p>
                {filteredImages.length > itemsPerPage && (
                  <small className="text-muted">
                    {itemsPerPage} images per page
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading gallery...</span>
              </div>
              <p className="mt-3 text-muted">Loading gallery images from SharePoint...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <h5>Error Loading Gallery</h5>
                <p className="mb-0">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Grid */}
        {!isLoading && (
          <div className="row g-4">
            {currentImages.length > 0 ? (
              currentImages.map((image) => (
                <div key={image.id} className="col-lg-4 col-md-6">
                  <div className="card h-100 shadow-sm" style={{minHeight: '400px'}}>
                    {/* Image */}
                    <div
                      className="card-img-top bg-light d-flex align-items-center justify-content-center"
                      style={{
                        height: '300px',
                        overflow: 'hidden',
                        padding: '8px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // Open image in lightbox or modal (future enhancement)
                        if (image.downloadUrl) {
                          window.open(image.downloadUrl, '_blank')
                        }
                      }}
                    >
                      {image.downloadUrl ? (
                        <img
                          src={image.downloadUrl}
                          alt={image.title || 'Gallery artwork'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            imageRendering: 'auto'
                          }}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            e.currentTarget.nextElementSibling?.classList.remove('d-none')
                          }}
                        />
                      ) : (
                        <div className="display-1">🖼️</div>
                      )}
                      {image.downloadUrl && (
                        <div className="display-1 d-none">🖼️</div>
                      )}
                    </div>

                    <div className="card-body d-flex flex-column">
                      {/* Image Info */}
                      <div className="mb-2">
                        <h5 className="card-title text-primary fw-bold mb-1">
                          {image.title || image.name}
                        </h5>
                        {image.description && (
                          <p className="card-text text-muted small mb-2">
                            {image.description}
                          </p>
                        )}
                      </div>

                      {/* Category */}
                      {image.category && (
                        <div className="mt-auto">
                          <span className="badge bg-light text-dark me-1 mb-1">
                            {image.category}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="alert alert-info text-center">
                  <h5>No artwork matches your search</h5>
                  <p className="mb-0">Try adjusting your search term or filters.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pagination Controls */}
        {!isLoading && totalPages > 1 && (
          <div className="row mt-5">
            <div className="col-12">
              <nav aria-label="Gallery pagination">
                <ul className="pagination justify-content-center">
                  {/* Previous Button */}
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <span aria-hidden="true">&laquo;</span> Previous
                    </button>
                  </li>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 10) {
                      pageNum = i + 1;
                    } else if (currentPage <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage > totalPages - 5) {
                      pageNum = totalPages - 9 + i;
                    } else {
                      pageNum = currentPage - 5 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    )
                  })}

                  {/* Next Button */}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next <span aria-hidden="true">&raquo;</span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* No items message */}
        {!isLoading && galleryImages.length === 0 && (
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="display-1 mb-4">🖼️</div>
              <h3 className="text-white">Gallery Coming Soon!</h3>
              <p className="text-white">Our artists are creating beautiful new works. Check back soon to see the latest additions!</p>
            </div>
          </div>
        )}

        {/* Artist Info Section */}
        <div className="row mt-5 pt-5 border-top">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h3 className="mb-3">Featured Local Artists</h3>
                <p className="text-dark mb-0">
                  The Salty Mermaid proudly showcases works from over 75 talented artists from the Ormond Beach area.
                  Each piece in our gallery represents the unique perspective and skill of our local creative community.
                </p>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-4 mb-3 text-center">
                <div className="text-primary">
                  <h4>75+</h4>
                  <p className="mb-0">Local Artists</p>
                </div>
              </div>
              <div className="col-md-4 mb-3 text-center">
                <div className="text-success">
                  <h4>200+</h4>
                  <p className="mb-0">Artworks</p>
                </div>
              </div>
              <div className="col-md-4 mb-3 text-center">
                <div className="text-info">
                  <h4>Monthly</h4>
                  <p className="mb-0">New Additions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ArtGallery
